/**
 * @breathly/shared — event kind definitions shared across all surfaces.
 *
 * Per STARTUP_PHILOSOPHY.md: extracted from duplicated inline definitions
 * in web and mobile dashboards once a third occurrence was needed. This is
 * the canonical source of truth for event kinds, their icons, and the
 * per-kind payload fields.
 */

// ---------------------------------------------------------------------------
// Event Kind Metadata
// ---------------------------------------------------------------------------

export interface EventKindMeta {
  readonly value: string;
  readonly label: string;
  readonly icon: string;
}

export const EVENT_KINDS: readonly EventKindMeta[] = [
  { value: 'meal', label: 'Meal', icon: '\u{1F37D}' },
  { value: 'beverage', label: 'Beverage', icon: '\u2615' },
  { value: 'hydration', label: 'Hydration', icon: '\u{1F4A7}' },
  { value: 'sleep', label: 'Sleep', icon: '\u{1F31F}' },
  { value: 'exercise', label: 'Exercise', icon: '\u{1F3C3}' },
  { value: 'symptom', label: 'Symptom', icon: '\u{1FA7A}' },
  { value: 'emotion', label: 'Emotion', icon: '\u{1F60A}' },
  { value: 'treatment', label: 'Treatment', icon: '\u{1F9D8}' },
  { value: 'journal', label: 'Journal', icon: '\u{1F4DD}' },
  { value: 'social', label: 'Social', icon: '\u{1F465}' },
  { value: 'stress', label: 'Stress', icon: '\u{1F4A2}' },
  { value: 'oral-care', label: 'Oral Care', icon: '\u{1FAA7}' },
  { value: 'medical', label: 'Medical', icon: '\u2695' },
  { value: 'travel', label: 'Travel', icon: '\u{1F6EB}' },
  { value: 'lifestyle', label: 'Lifestyle', icon: '\u{1F3E0}' },
  { value: 'environment', label: 'Environment', icon: '\u{1F30D}' },
  { value: 'observation', label: 'Observation', icon: '\u{1F50D}' },
] as const;

/** Lookup an event's icon by kind value. */
export function kindIcon(kind: string): string {
  return EVENT_KINDS.find((k) => k.value === kind)?.icon ?? '\u{1F4CC}';
}

// ---------------------------------------------------------------------------
// Kind-Specific Payload Fields
// ---------------------------------------------------------------------------

export interface KindField {
  readonly key: string;
  readonly label: string;
  readonly type: 'number' | 'select';
  readonly min?: number;
  readonly max?: number;
  readonly step?: number;
  readonly options?: readonly string[];
}

export const KIND_FIELDS: Record<string, readonly KindField[]> = {
  sleep: [
    { key: 'hours', label: 'Hours', type: 'number', min: 0, max: 24, step: 0.5 },
    { key: 'quality', label: 'Quality', type: 'select', options: ['Deep', 'Light', 'Restless', 'Uninterrupted'] as const },
  ],
  meal: [
    { key: 'meal_type', label: 'Meal type', type: 'select', options: ['Breakfast', 'Lunch', 'Dinner', 'Snack'] as const },
  ],
  exercise: [
    { key: 'exercise_type', label: 'Exercise type', type: 'select', options: ['Walking', 'Running', 'Cycling', 'Swimming', 'Yoga', 'Strength', 'Other'] as const },
    { key: 'duration_minutes', label: 'Duration (min)', type: 'number', min: 0, step: 5 },
    { key: 'distance_km', label: 'Distance (km)', type: 'number', min: 0, step: 0.1 },
    { key: 'calories_est', label: 'Calories (est.)', type: 'number', min: 0, step: 10 },
  ],
  beverage: [
    { key: 'beverage_type', label: 'Type', type: 'select', options: ['Coffee', 'Tea', 'Juice', 'Soda', 'Alcohol', 'Water', 'Other'] as const },
    { key: 'volume_ml', label: 'Volume (ml)', type: 'number', min: 0, step: 50 },
  ],
  hydration: [
    { key: 'volume_ml', label: 'Volume (ml)', type: 'number', min: 0, step: 50 },
  ],
  symptom: [
    { key: 'symptom_type', label: 'Symptom type', type: 'select', options: ['Headache', 'Fatigue', 'Nausea', 'Dizziness', 'Pain', 'Congestion', 'Skin', 'Digestive', 'Other'] as const },
    { key: 'severity', label: 'Severity (1-10)', type: 'number', min: 1, max: 10 },
  ],
  emotion: [
    { key: 'mood', label: 'Mood', type: 'select', options: ['Happy', 'Sad', 'Anxious', 'Calm', 'Angry', 'Excited', 'Tired', 'Grateful', 'Stressed', 'Neutral'] as const },
    { key: 'intensity', label: 'Intensity (1-10)', type: 'number', min: 1, max: 10 },
  ],
  stress: [
    { key: 'severity', label: 'Severity (1-10)', type: 'number', min: 1, max: 10 },
  ],
  treatment: [
    { key: 'treatment_type', label: 'Type', type: 'select', options: ['Medication', 'Therapy', 'Massage', 'Acupuncture', 'Breathing', 'Other'] as const },
  ],
  'oral-care': [
    { key: 'oral_care_type', label: 'Care type', type: 'select', options: ['Brushing', 'Flossing', 'Mouthwash', 'Checkup', 'Cleaning', 'Other'] as const },
  ],
  medical: [
    { key: 'medical_type', label: 'Type', type: 'select', options: ['Checkup', 'Test', 'Vaccination', 'Consultation', 'Procedure', 'Other'] as const },
  ],
  social: [
    { key: 'social_type', label: 'Type', type: 'select', options: ['Family', 'Friends', 'Colleagues', 'Community', 'Date', 'Party', 'Other'] as const },
  ],
  journal: [
    { key: 'journal_type', label: 'Type', type: 'select', options: ['Reflection', 'Gratitude', 'Idea', 'Dream', 'Goal', 'Other'] as const },
  ],
  travel: [
    { key: 'travel_type', label: 'Type', type: 'select', options: ['Commute', 'Road trip', 'Flight', 'Walking', 'Public transit', 'Other'] as const },
  ],
} as const;

// ---------------------------------------------------------------------------
// Quick-Log Templates (most common actions)
// ---------------------------------------------------------------------------

export interface QuickLogTemplate {
  readonly kind: string;
  readonly payload: Record<string, unknown>;
  readonly label: string;
  readonly icon: string;
}

export const QUICK_LOG_TEMPLATES: readonly QuickLogTemplate[] = [
  { kind: 'hydration', payload: { volume_ml: 250 }, label: 'Water', icon: '\u{1F4A7}' },
  { kind: 'meal', payload: { meal_type: 'Snack' }, label: 'Snack', icon: '\u{1F37D}' },
  { kind: 'beverage', payload: { beverage_type: 'Coffee', volume_ml: 200 }, label: 'Coffee', icon: '\u2615' },
  { kind: 'exercise', payload: { exercise_type: 'Walking', duration_minutes: 30 }, label: 'Walk 30m', icon: '\u{1F3C3}' },
  { kind: 'sleep', payload: { hours: 7.5 }, label: 'Sleep 7.5h', icon: '\u{1F31F}' },
  { kind: 'oral-care', payload: { oral_care_type: 'Brushing' }, label: 'Brushed', icon: '\u{1FAA7}' },
  { kind: 'symptom', payload: { symptom_type: 'Other', severity: 5 }, label: 'Symptom', icon: '\u{1FA7A}' },
] as const;
